import React, { useMemo, useState } from "react";
import Alert from "../../components/common/Alert";
import Card from "../../components/common/Card";
import FilterBar, {
  formatDateInputValue,
} from "../../components/common/FilterBar";
import PageHeader from "../../components/common/PageHeader";
import StatCard from "../../components/common/StatCard";
import { useGetBusinessesQuery, useGetSalesSummaryQuery } from "../../store/api";
import {
  formatCurrency,
  formatNumber,
  getDefaultReportingFilters,
  getPresetDateRange,
  getStoreOptionsFromBusinesses,
  getUtcRangeFromDateInputs,
} from "../../utils/reporting";

const SalesSummary = () => {
  const { data: businesses } = useGetBusinessesQuery();
  const storeOptions = useMemo(
    () => getStoreOptionsFromBusinesses(businesses),
    [businesses],
  );
  const [draftFilters, setDraftFilters] = useState(
    getDefaultReportingFilters(),
  );
  const [appliedFilters, setAppliedFilters] = useState(
    getDefaultReportingFilters(),
  );

  const queryArgs = useMemo(() => {
    const range = getUtcRangeFromDateInputs(appliedFilters);
    return {
      storeIds: appliedFilters.storeIds,
      from: range.from,
      to: range.to,
    };
  }, [appliedFilters]);

  const { data, isLoading, isFetching, isError, error } =
    useGetSalesSummaryQuery(queryArgs);

  const currencyCode = data?.currencyCode || "USD";
  const datePresets = [
    { value: "today", label: "Today" },
    { value: "last7", label: "Last 7 Days" },
    { value: "last30", label: "Last 30 Days" },
  ];
  const activePreset =
    datePresets.find((preset) => {
      const range = getPresetDateRange(preset.value);
      return draftFilters.from === range.from && draftFilters.to === range.to;
    })?.value || "";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sales Summary"
        subtitle="Aggregated completed-order revenue across selected stores."
      />

      <Card>
        <div className="mb-6">
          <FilterBar
            startDate={draftFilters.from}
            endDate={draftFilters.to}
            onStartDateChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                from: value ? formatDateInputValue(value) : "",
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            onEndDateChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                to: value ? formatDateInputValue(value) : "",
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            stores={storeOptions}
            storeId={draftFilters.storeIds[0] || ""}
            onStoreChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                storeIds: value ? [value] : [],
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            presets={datePresets}
            activePreset={activePreset}
            onPresetChange={(presetKey) => {
              const range = getPresetDateRange(presetKey);
              const nextFilters = {
                ...draftFilters,
                from: range.from,
                to: range.to,
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            onReset={() => {
              const next = getDefaultReportingFilters();
              setDraftFilters(next);
              setAppliedFilters(next);
            }}
            isLoading={isFetching}
          />
        </div>

        {isError && (
          <Alert tone="error">
            {error?.data?.detail || "Failed to load sales summary."}
          </Alert>
        )}

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4, 5, 6, 7].map((value) => (
              <div
                key={value}
                className="h-32 animate-pulse rounded-xl bg-slate-100"
              />
            ))}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Orders" value={formatNumber(data?.ordersCount)} />
            <StatCard
              label="Gross Sales"
              value={formatCurrency(data?.grossSales, currencyCode)}
              accent="emerald"
            />
            <StatCard
              label="Tax"
              value={formatCurrency(data?.taxTotal, currencyCode)}
              accent="blue"
            />
            <StatCard
              label="Fees"
              value={formatCurrency(data?.feeTotal, currencyCode)}
              accent="amber"
            />
            <StatCard
              label="Discounts"
              value={formatCurrency(data?.discountTotal, currencyCode)}
              accent="rose"
            />
            <StatCard
              label="Net Sales"
              value={formatCurrency(data?.netSales, currencyCode)}
              accent="indigo"
            />
            <StatCard
              label="Average Order Value"
              value={formatCurrency(data?.averageOrderValue, currencyCode)}
              accent="slate"
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default SalesSummary;
